import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PhoneIncoming, UserPlus, X, ArrowRight } from "lucide-react";
import { AppButton } from "../ui";

/**
 * Screen-pop for an inbound call.
 *
 * The point is the seconds before the counsellor picks up: who is this, have we
 * spoken before, and what did we last say. Landing on the enquiry *first* is the
 * entire value of connecting a CRM to a phone line — otherwise they answer blind
 * and ask the student to repeat everything.
 *
 * Mounted once globally in App.js, driven by the `vc-incoming-call` window event
 * that SocketContext dispatches. It is deliberately not a toast: toasts stack,
 * auto-dismiss and are easy to miss, and a ringing phone is none of those things.
 *
 * ── Converted to Tailwind, 2026-08-25 (Phase 5). incoming-call-popup.scss deleted
 * in the same commit, per the atomic rule (TOKEN_CONTRACT §6.1). Four things the
 * stylesheet was doing that are worth knowing:
 *
 *  1. **The accent was `#0ea5e9` in four places.** That is `--chart-5` to the byte
 *     (199 89% 48%), so the popup's identity colour is a token now and follows the
 *     theme. It is deliberately NOT `--primary`: the sky accent is what
 *     distinguishes an inbound screen-pop from the outbound call-status toast,
 *     which can be on screen at the same time.
 *  2. **The "Open" button was hand-rolled** — its own padding, its own radius, its
 *     own hover, in a sky that no other button in the app uses. It is `AppButton`
 *     (1,141 adopters), so it now carries the institute's brand and the app's 8px
 *     radius. This is the "a bespoke button two files from the shared one" finding
 *     from the filter sweep, in a different module.
 *  3. **The shadow was `rgba(0, 0, 0, 0.18)`** — light-only, on a surface that
 *     floats. It is `--popover-shadow` now, a new token, because a card shadow is
 *     too weak for this and 27 other sheets hardcode a near-identical one.
 *  4. **Centring was baked into the keyframes.** `translate(-50%, …)` in both
 *     `from` and `to` meant the animation and the horizontal centring fought over
 *     one transform property. The wrapper centres; the card animates.
 */

// Long enough to cover ringing plus a moment to react; short enough that a
// forgotten card doesn't sit on screen all afternoon.
const AUTO_DISMISS_MS = 45_000;

export default function IncomingCallPopup() {
  const navigate = useNavigate();
  const [call, setCall] = useState(null);
  const timerRef = useRef(null);

  const dismiss = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setCall(null);
  }, []);

  useEffect(() => {
    const onIncoming = (e) => {
      const detail = e.detail || {};
      setCall(detail);

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCall(null), AUTO_DISMISS_MS);
    };

    window.addEventListener("vc-incoming-call", onIncoming);
    return () => {
      window.removeEventListener("vc-incoming-call", onIncoming);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Escape dismisses, like every other transient overlay in the app.
  useEffect(() => {
    if (!call) return undefined;
    const onKey = (e) => { if (e.key === "Escape") dismiss(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [call, dismiss]);

  if (!call) return null;

  const open = () => {
    if (call.link) navigate(call.link);
    dismiss();
  };

  return (
    /* Top-centre rather than bottom-right: it must not share space with the
       click-to-call status toast, which can legitimately be on screen at the same
       time (a counsellor mid-outbound-call gets an inbound one). z-1090 puts it
       above that toast. The wrapper owns the centring so the card's animation has
       `transform` to itself. */
    <div className="tw-fixed tw-top-4 tw-left-1/2 -tw-translate-x-1/2 tw-z-critical tw-w-[min(440px,calc(100vw-1.5rem))] max-[576px]:tw-top-2 max-[576px]:tw-w-[calc(100vw-1rem)]">
      <div
        role="alert"
        aria-live="assertive"
        className="tw-flex tw-items-center tw-gap-3 tw-py-3 tw-px-3.5 tw-border tw-border-line tw-border-l-[3px] tw-border-l-chart-5 tw-rounded tw-bg-surface-raised tw-text-content tw-shadow-popover tw-animate-drop-in motion-reduce:tw-animate-none"
      >
        {/* The ring that keeps ringing. `content-['']` is required on the same
            element as the before: variant — Tailwind's rule, and the pseudo-element
            renders nothing without it. */}
        <div className="tw-relative tw-flex tw-items-center tw-justify-center tw-shrink-0 tw-w-[34px] tw-h-[34px] tw-rounded-full tw-bg-chart-5/[0.12] tw-text-chart-5 before:tw-content-[''] before:tw-absolute before:tw-inset-0 before:tw-rounded-full before:tw-border-2 before:tw-border-chart-5/55 before:tw-animate-ring-out motion-reduce:before:tw-animate-none motion-reduce:before:tw-opacity-50">
          <PhoneIncoming size={18} />
        </div>

        <div className="tw-flex tw-flex-col tw-gap-[0.1rem] tw-min-w-0 tw-flex-1">
          <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-[0.625rem] tw-font-bold tw-uppercase tw-tracking-[0.06em] tw-text-content-subtle">
            Incoming call
            {call.isNewLead && (
              <span className="tw-inline-flex tw-items-center tw-gap-[0.2rem] tw-py-[0.05rem] tw-px-[0.35rem] tw-rounded-full tw-bg-success-soft tw-text-success-fg tw-tracking-[0.02em]">
                <UserPlus size={11} /> New lead
              </span>
            )}
          </span>
          <span className="tw-text-[0.9375rem] tw-font-semibold tw-leading-[1.25] tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">
            {call.message || call.number}
          </span>
          {/* Hidden below Bootstrap's `sm` (576px), NOT Tailwind's (640px). */}
          {call.isNewLead && (
            <span className="tw-text-[0.6875rem] tw-leading-[1.4] tw-text-content-muted max-[576px]:tw-hidden">
              We haven't spoken to this number before — an enquiry has been created for them.
            </span>
          )}
        </div>

        <div className="tw-flex tw-items-center tw-gap-1.5 tw-shrink-0">
          {call.link && (
            <AppButton size="sm" onClick={open}>
              Open <ArrowRight size={13} />
            </AppButton>
          )}
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            className="tw-p-[0.2rem] tw-border-0 tw-bg-transparent tw-leading-none tw-text-content-subtle tw-cursor-pointer hover:tw-text-content"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
